import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Wind, Gauge, Timer, Weight, CheckCircle2, AlertCircle,
  Info, ChevronRight, Save, RotateCcw, Microscope,
  TrendingUp, BarChart3, Zap, Thermometer, CircleDot,
  Factory, Milk, FishSymbol, ShieldAlert, Droplets,
  ArrowDown, ArrowRightLeft, Waves, FlaskConical,
  Settings, Beaker, Filter, Layers, ArrowRight, Database
} from "lucide-react";
import { useSampleLoader, fmt as sharedFmt } from '@/lib/equipmentSpecUtils';
import { SampleSelector } from '@/components/SampleSelector';
import PageHeader from '@/components/PageHeader';
import { ScienceAccordion } from '@/components/ScienceAccordion';

/* ─── Aeration / Activated Sludge Specification ───────────
   Full physics: Monod kinetics, OUR, SRT, F/M, oxygen transfer,
   nitrification, biomass inventory
   ────────────────────────────────────────────────────── */

interface AerationParams {
  // Influent
  flowRate: number;        // m³/day
  influentBOD: number;     // mg/L
  influentCOD: number;     // mg/L
  influentTSS: number;     // mg/L
  influentTKN: number;     // mg/L
  influentNH3: number;     // mg/L
  influentpH: number;      // pH
  temperature: number;     // °C
  og: number;
  ph: number;
  alkalinity: number;
  turbidity: number;
  conductivity: number;
  hardness: number;
  nh3n: number;
  tp: number;
  // Tank
  tankLength: number;       // m
  tankWidth: number;        // m
  tankDepth: number;        // m
  freeboard: number;        // m
  // Process
  processType: 'activated_sludge' | 'mbbr' | 'extended_aeration';
  mlssTarget: number;      // mg/L
  doTarget: number;         // mg/L
  alphaFactor: number;      // 0.3-1.0
  // Kinetics
  yieldCoeff: number;       // kg MLSS/kg BOD
  decayRate: number;        // d⁻¹
  halfSaturation: number;   // mg/L BOD (Ks)
  maxGrowthRate: number;    // d⁻¹ (μmax)
  // Nitrification
  nitrificationEnabled: boolean;
  nitrificationRate: number; // d⁻¹
}

interface AerationSize {
  tankArea: number;
  totalVolume: number;
  workingVolume: number;
  actualDetention: number; // h
  hlr: number;              // m³/m²·h
  // Biomass
  fmRatio: number;          // kg BOD/kg MLSS·d
  srt: number;              // days
  biomassInventory: number; // kg MLSS
  biomassProduction: number; // kg/day
  // Removal
  bodRemoval: number;      // fraction
  codRemoval: number;       // fraction
  tssRemoval: number;       // fraction
  nitrificationEfficiency: number; // fraction
  effluentBOD: number;       // mg/L
  effluentCOD: number;      // mg/L
  effluentTSS: number;      // mg/L
  effluentNH3: number;      // mg/L
  // Oxygen
  our: number;             // mg O₂/L·h
  sor: number;             // kg O₂/day
  airFlowRate: number;      // m³/min
  diffuserCount: number;
  // Power
  blowerPower: number;      // kW
  mixerPower: number;       // kW
  totalPower: number;       // kW
  // Status
  status: 'optimal' | 'acceptable' | 'undersized' | 'oversized' | 'overloaded';
  statusDetail: string;
  designNotes: string[];
}

const PROFILES = [
  { name: 'Dairy Effluent', description: 'High BOD, requires extended aeration', icon: Milk,
    defaults: { flowRate: 250, influentBOD: 1850, influentCOD: 3200, influentTSS: 450, influentTKN: 85, influentNH3: 45, influentpH: 7.0, temperature: 25,
      og: 120, ph: 6.8, alkalinity: 180, turbidity: 380, conductivity: 2500, hardness: 250, nh3n: 45, tp: 12,
      tankLength: 12, tankWidth: 6, tankDepth: 4.5, freeboard: 0.5,
      processType: 'extended_aeration' as const, mlssTarget: 3500, doTarget: 2.0, alphaFactor: 0.85,
      yieldCoeff: 0.6, decayRate: 0.06, halfSaturation: 50, maxGrowthRate: 3.5,
      nitrificationEnabled: true, nitrificationRate: 0.35 } },
  { name: 'Brewery Effluent', description: 'Very high COD, strong nitrification needed', icon: Factory,
    defaults: { flowRate: 350, influentBOD: 2200, influentCOD: 4100, influentTSS: 620, influentTKN: 120, influentNH3: 65, influentpH: 6.5, temperature: 20,
      og: 180, ph: 5.2, alkalinity: 80, turbidity: 550, conductivity: 4200, hardness: 350, nh3n: 85, tp: 18,
      tankLength: 15, tankWidth: 8, tankDepth: 5, freeboard: 0.5,
      processType: 'activated_sludge' as const, mlssTarget: 3000, doTarget: 2.5, alphaFactor: 0.75,
      yieldCoeff: 0.65, decayRate: 0.05, halfSaturation: 60, maxGrowthRate: 4.0,
      nitrificationEnabled: true, nitrificationRate: 0.4 } },
  { name: 'Fish Processing', description: 'High TSS, moderate BOD', icon: FishSymbol,
    defaults: { flowRate: 150, influentBOD: 1450, influentCOD: 2650, influentTSS: 520, influentTKN: 70, influentNH3: 35, influentpH: 7.2, temperature: 15,
      og: 220, ph: 6.9, alkalinity: 220, turbidity: 480, conductivity: 1800, hardness: 180, nh3n: 35, tp: 8,
      tankLength: 10, tankWidth: 5, tankDepth: 4, freeboard: 0.5,
      processType: 'activated_sludge' as const, mlssTarget: 2500, doTarget: 2.0, alphaFactor: 0.8,
      yieldCoeff: 0.55, decayRate: 0.07, halfSaturation: 40, maxGrowthRate: 3.0,
      nitrificationEnabled: true, nitrificationRate: 0.3 } },
];

const fmt = (v: any, d = 1) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

export function calcAeration(p: AerationParams): AerationSize {
  const Q = p.flowRate; // m³/day
  const q = Q / 24; // m³/h

  // Geometry
  const tankArea = p.tankLength * p.tankWidth;
  const workingVolume = tankArea * p.tankDepth;
  const totalVolume = tankArea * (p.tankDepth + p.freeboard);
  const detention = (workingVolume / q); // h
  const hlr = q / tankArea;

  // Water properties
  const T = p.temperature;
  const tempK = T + 273.15;
  const muWater = 0.001 * Math.exp(-1.5 * (T - 20) / tempK); // Pa·s
  const rhoWater = 998 - 0.2 * (T - 20);

  // Monod kinetics for substrate removal
  // μ = μmax * S / (Ks + S)
  const S0 = p.influentBOD; // mg/L
  const X = p.mlssTarget; // mg/L
  const mu = p.maxGrowthRate * S0 / (p.halfSaturation + S0); // d⁻¹ actual growth rate
  const muCorrected = mu * Math.pow(1.07, T - 20); // temperature correction

  // Specific substrate utilization rate: U = (μ / Y) + kd
  const U = (muCorrected / p.yieldCoeff) + p.decayRate; // kg BOD/kg MLSS·d
  const fmRatio = (S0 * Q / 1000) / (X * workingVolume / 1000); // kg BOD/kg MLSS·d

  // SRT calculation: θc = X / (Y * U * X - kd * X)
  // Simplified: θc = 1 / (Y * U - kd)
  const srt = 1 / Math.max(0.001, p.yieldCoeff * U - p.decayRate);

  // BOD removal efficiency (Monod-based)
  const S_eff = p.halfSaturation * p.decayRate / (p.yieldCoeff * muCorrected - p.decayRate);
  const bodRemoval = Math.min(0.98, Math.max(0.1, 1 - S_eff / S0));
  const effluentBOD = S0 * (1 - bodRemoval);

  // COD removal (typically slightly lower than BOD)
  const codRemoval = bodRemoval * 0.92;
  const effluentCOD = p.influentCOD * (1 - codRemoval);

  // TSS removal
  const tssRemoval = Math.min(0.95, bodRemoval * 0.95);
  const effluentTSS = p.influentTSS * (1 - tssRemoval);

  // Nitrification (Monod for ammonia)
  let nitrificationEfficiency = 0;
  let effluentNH3 = p.influentNH3;
  if (p.nitrificationEnabled) {
    const mu_nit = p.nitrificationRate * Math.pow(1.07, T - 20) * p.influentNH3 / (1 + p.influentNH3); // d⁻¹
    const nit_removal = Math.min(0.99, 1 - 1 / (mu_nit * srt));
    nitrificationEfficiency = nit_removal;
    effluentNH3 = p.influentNH3 * (1 - nit_removal);
  }

  // Biomass inventory and production
  const biomassInventory = X * workingVolume / 1000; // kg MLSS
  const biomassProduction = p.yieldCoeff * (S0 - effluentBOD) * Q / 1000; // kg/day

  // Oxygen demand (OUR)
  // OUR = a' * (S0 - S) + b' * X
  // where a' = 0.5 kg O2/kg BOD, b' = 0.1 kg O2/kg MLSS·d
  const a_prime = 0.5;
  const b_prime = 0.1;
  const our_mg_L_h = a_prime * (S0 - effluentBOD) / 24 + b_prime * X / 24; // mg/L·h
  const sor = our_mg_L_h * workingVolume / 1000 * 24; // kg O2/day

  // Oxygen transfer (SOTR)
  // SOTE depends on alpha, temperature, depth
  const sote = 0.25 * p.alphaFactor * Math.pow(1.02, 20 - T) * (p.tankDepth / 4); // fraction
  const sotr = sor / Math.max(sote, 0.05); // kg O2/day required at standard conditions

  // Air flow rate (at standard conditions, 1.2 kg/m³ density)
  const airDensity = 1.2; // kg/m³ at 20°C
  const o2InAir = 0.232; // fraction
  const airFlowRate = (sotr / 24 / 60) / (airDensity * o2InAir); // m³/min

  // Diffuser count (assume 2 m³/h per diffuser)
  const diffuserFlow = 2; // m³/h per diffuser
  const diffuserCount = Math.ceil((airFlowRate * 60) / diffuserFlow);

  // Power calculations
  // Blower: P = (Q_air × ΔP) / (η × 1000)
  const deltaP = 50000; // Pa (typical diffuser pressure drop)
  const etaBlower = 0.75;
  const blowerPower = (airFlowRate / 60 * deltaP) / (etaBlower * 1000); // kW
  // Mixer: assume 0.02 kW/m³ for activated sludge
  const mixerPower = 0.02 * workingVolume; // kW
  const totalPower = blowerPower + mixerPower;

  // Status
  let status: AerationSize['status']; let statusDetail: string;
  const designNotes: string[] = [];

  if (fmRatio >= 0.05 && fmRatio <= 0.3 && srt >= 3 && srt <= 20 && bodRemoval >= 0.85) {
    status = 'optimal';
    statusDetail = `Optimal. F/M ${fmRatio.toFixed(2)} kg/kg·d, SRT ${srt.toFixed(1)} d, BOD removal ${(bodRemoval*100).toFixed(0)}%.`;
  } else if (fmRatio >= 0.02 && fmRatio <= 0.5 && srt >= 2 && bodRemoval >= 0.70) {
    status = 'acceptable';
    statusDetail = `Acceptable. F/M ${fmRatio.toFixed(2)} kg/kg·d. Monitor SRT and effluent.`;
  } else if (fmRatio > 1.0 || srt < 1 || bodRemoval < 0.50) {
    status = 'overloaded';
    statusDetail = `Overloaded. F/M ${fmRatio.toFixed(2)} or SRT ${srt.toFixed(1)} too low. Increase volume or MLSS.`;
  } else if (workingVolume < 50) {
    status = 'undersized';
    statusDetail = `Undersized. Volume ${workingVolume.toFixed(0)} m³. Increase tank size.`;
  } else {
    status = 'oversized';
    statusDetail = `Oversized. Consider reducing for cost savings.`;
  }

  if (bodRemoval < 0.80) designNotes.push('⚠️ BOD removal below target. Increase SRT or MLSS concentration.');
  if (p.nitrificationEnabled && nitrificationEfficiency < 0.70) designNotes.push('⚠️ Nitrification efficiency low. Check DO and SRT.');
  if (srt < 3 && p.processType === 'extended_aeration') designNotes.push('⚠️ SRT too short for extended aeration. Minimum 15 days required.');
  if (p.doTarget < 1.5) designNotes.push('⚠️ DO setpoint below 1.5 mg/L. Risk of filamentous growth.');
  if (p.temperature < 10) designNotes.push('ℹ️ Low temperature reduces biological activity. Consider insulation.');
  if (designNotes.length === 0) designNotes.push('✅ All design parameters within recommended ranges.');

  return {
    tankArea, totalVolume, workingVolume,
    actualDetention: detention, hlr,
    fmRatio, srt, biomassInventory, biomassProduction,
    bodRemoval, codRemoval, tssRemoval, nitrificationEfficiency,
    effluentBOD, effluentCOD, effluentTSS, effluentNH3,
    our: our_mg_L_h, sor, airFlowRate, diffuserCount,
    blowerPower, mixerPower, totalPower,
    status, statusDetail, designNotes,
  };
}


/* ─── UI Components ──────────────────────────────────── */
function S({ label, value, min, max, step, unit, icon: Icon, color = '#06b6d4', onChange, info }: any) {
  const safeValue = typeof value === 'number' && !isNaN(value) ? value : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color }} />
          <span className="text-xs font-medium text-slate-300">{label}</span>
          {info && <div className="group relative"><Info className="w-3 h-3 text-slate-600 cursor-help" /><div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 rb-card-sm">{info}</div></div>}
        </div>
        <span className="text-xs font-bold text-white tabular-nums">{step < 0.1 ? safeValue.toFixed(2) : step < 1 ? safeValue.toFixed(1) : safeValue.toFixed(0)}<span className="text-[10px] text-slate-500 font-normal ml-0.5">{unit}</span></span>
      </div>
      <input type="range" min={min} max={max} step={step} value={safeValue} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer" style={{ accentColor: color }} />
    </div>
  );
}

function G({ value, max, label, unit, color }: any) {
  const safeValue = typeof value === 'number' && !isNaN(value) ? value : 0;
  const safeMax = typeof max === 'number' && max > 0 ? max : 1;
  const pct = Math.min(100, (safeValue / safeMax) * 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px]"><span className="text-slate-500">{label}</span><span className="font-bold" style={{ color }}>{fmt(safeValue, 1)} {unit}</span></div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} className="h-full rounded-full" style={{ backgroundColor: color }} /></div>
    </div>
  );
}

function PC({ profile, isActive, onClick }: any) {
  const Icon = profile.icon;
  return (
    <button onClick={onClick} className={`w-full text-left p-3 rounded-xl border transition-all ${isActive ? 'bg-amber-500/10 border-amber-500/30' : 'bg-rb-elevated border-slate-800 hover:border-slate-700'}`}>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? 'bg-amber-500/20' : 'bg-slate-800'}`}><Icon className={`w-4 h-4 ${isActive ? 'text-amber-400' : 'text-slate-500'}`} /></div>
        <div><p className={`text-xs font-semibold ${isActive ? 'text-amber-400' : 'text-slate-300'}`}>{profile.name}</p><p className="text-[10px] text-slate-500 leading-tight">{profile.description}</p></div>
      </div>
    </button>
  );
}

/* ─── Flow Diagram ───────────────────────────────────── */
function FlowDiagram({ params, size }: { params: AerationParams; size: AerationSize }) {
  const safe = {
    Q: typeof params.flowRate === 'number' ? params.flowRate / 24 : 0,
    detention: typeof size?.actualDetention === 'number' ? size.actualDetention : 0,
    fm: typeof size?.fmRatio === 'number' ? size.fmRatio : 0,
    srt: typeof size?.srt === 'number' ? size.srt : 0,
    do: typeof params.doTarget === 'number' ? params.doTarget : 0,
    our: typeof size?.our === 'number' ? size.our : 0,
    effluentBOD: typeof size?.effluentBOD === 'number' ? size.effluentBOD : 0,
    airFlow: typeof size?.airFlowRate === 'number' ? size.airFlowRate : 0,
    power: typeof size?.totalPower === 'number' ? size.totalPower : 0,
    diffusers: typeof size?.diffuserCount === 'number' ? size.diffuserCount : 0,
    biomass: typeof size?.biomassInventory === 'number' ? size.biomassInventory : 0,
  };

  return (
    <div className="p-4 rb-card-sm">
      <p className="text-[10px] text-slate-500 mb-3 uppercase tracking-wider font-bold flex items-center gap-2">
        <ArrowRightLeft className="w-3 h-3" /> Process Flow — Influent → Aeration → Effluent
      </p>
      <svg viewBox="0 0 900 380" className="w-full h-auto">
        <defs>
          <linearGradient id="waterGradA" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--rb-active)" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#0f2847" stopOpacity="0.8" />
          </linearGradient>
        </defs>
        <text x="450" y="18" fill="#94a3b8" fontSize="11" textAnchor="middle" fontWeight="bold">ACTIVATED SLUDGE — AERATION PROCESS TRAIN</text>

        {/* Influent */}
        <rect x="30" y="55" width="100" height="80" rx="6" fill="url(#waterGradA)" stroke="#f59e0b" strokeWidth="2" />
        <text x="80" y="72" fill="#f59e0b" fontSize="9" textAnchor="middle" fontWeight="bold">Influent</text>
        <text x="80" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">{fmt(params.flowRate, 0)} m³/d</text>
        <text x="80" y="100" fill="#f59e0b" fontSize="8" textAnchor="middle">BOD {fmt(params.influentBOD, 0)} mg/L</text>
        <text x="80" y="112" fill="#f59e0b" fontSize="8" textAnchor="middle">COD {fmt(params.influentCOD, 0)} mg/L</text>
        <text x="80" y="124" fill="#94a3b8" fontSize="8" textAnchor="middle">TSS {fmt(params.influentTSS, 0)} mg/L</text>

        {/* Arrow */}
        <line x1="132" y1="95" x2="170" y2="95" stroke="#f59e0b" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="168,92 175,95 168,98" fill="#f59e0b" />

        {/* Aeration Tank */}
        <rect x="180" y="40" width="300" height="110" rx="6" fill="url(#waterGradA)" stroke="#06b6d4" strokeWidth="2" />
        <text x="330" y="58" fill="#06b6d4" fontSize="10" textAnchor="middle" fontWeight="bold">Aeration Tank</text>
        <text x="330" y="74" fill="#94a3b8" fontSize="8" textAnchor="middle">{fmt(params.tankLength, 1)} × {fmt(params.tankWidth, 1)} × {fmt(params.tankDepth, 1)} m | Vol {fmt(size.workingVolume, 0)} m³</text>
        <text x="330" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">Detention {fmt(safe.detention, 1)} h | HLR {fmt(size.hlr, 1)} m³/m²·h</text>
        <text x="330" y="102" fill="#f59e0b" fontSize="8" textAnchor="middle">MLSS {fmt(params.mlssTarget, 0)} mg/L | F/M {fmt(safe.fm, 2)} | SRT {fmt(safe.srt, 1)} d</text>
        <text x="330" y="116" fill="#94a3b8" fontSize="8" textAnchor="middle">OUR {fmt(safe.our, 1)} mg/L·h | Air {fmt(safe.airFlow, 1)} m³/min | {fmt(safe.diffusers, 0)} diffusers</text>

        {/* Bubbles / diffusers */}
        {Array.from({ length: 8 }).map((_, i) => (
          <motion.circle key={i} cx={200 + i * 34} cy="130" r="2.5" fill="#81D4FA" opacity="0.5"
            animate={{ cy: [130, 55, 50], opacity: [0.5, 0.2, 0] }}
            transition={{ repeat: Infinity, duration: 2 + (i % 3), delay: i * 0.15 }} />
        ))}

        {/* MLSS particles */}
        {Array.from({ length: 12 }).map((_, i) => (
          <circle key={`p${i}`} cx={200 + (i % 6) * 45 + Math.random() * 10} cy={75 + (i % 3) * 15} r="1.5" fill="#a855f7" opacity="0.3" />
        ))}

        {/* Arrow to clarifier */}
        <line x1="482" y1="95" x2="520" y2="95" stroke="#06b6d4" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="518,92 525,95 518,98" fill="#06b6d4" />

        {/* Clarifier */}
        <rect x="530" y="55" width="100" height="80" rx="6" fill="url(#waterGradA)" stroke="#8b5cf6" strokeWidth="2" />
        <text x="580" y="72" fill="#8b5cf6" fontSize="9" textAnchor="middle" fontWeight="bold">Clarifier</text>
        <text x="580" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">Settling + RAS</text>
        <text x="580" y="100" fill="#8b5cf6" fontSize="8" textAnchor="middle">RAS to aeration</text>

        {/* Arrow to effluent */}
        <line x1="632" y1="75" x2="680" y2="75" stroke="#10b981" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="678,72 685,75 678,78" fill="#10b981" />

        {/* Effluent */}
        <rect x="690" y="55" width="100" height="80" rx="6" fill="url(#waterGradA)" stroke="#10b981" strokeWidth="2" />
        <text x="740" y="72" fill="#10b981" fontSize="9" textAnchor="middle" fontWeight="bold">Effluent</text>
        <text x="740" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">BOD {fmt(safe.effluentBOD, 0)} mg/L</text>
        <text x="740" y="100" fill="#10b981" fontSize="8" textAnchor="middle">BOD↓ {(size.bodRemoval * 100).toFixed(0)}%</text>
        <text x="740" y="112" fill="#94a3b8" fontSize="8" textAnchor="middle">Power {fmt(safe.power, 1)} kW</text>

        {/* Blower */}
        <g transform="translate(720, 150)">
          <rect x="0" y="0" width="40" height="25" rx="3" fill="#0f172a" stroke="#06b6d4" strokeWidth="1.5" />
          <text x="20" y="16" fill="#06b6d4" fontSize="7" textAnchor="middle">Blower</text>
          <line x1="-8" y1="12" x2="0" y2="12" stroke="#06b6d4" strokeWidth="1.5" />
        </g>
        <line x1="720" y1="162" x2="330" y2="162" stroke="#06b6d4" strokeWidth="1.5" strokeDasharray="4 2" />
        <line x1="330" y1="162" x2="330" y2="150" stroke="#06b6d4" strokeWidth="1.5" strokeDasharray="4 2" />

        {/* RAS line */}
        <line x1="580" y1="135" x2="580" y2="180" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="4 2" />
        <line x1="580" y1="180" x2="330" y2="180" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="4 2" />
        <line x1="330" y1="180" x2="330" y2="150" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="4 2" />
        <polygon points="327,152 330,145 333,152" fill="#f59e0b" />
        <text x="455" y="185" fill="#f59e0b" fontSize="7" textAnchor="middle">RAS Return Activated Sludge</text>

        {/* Sludge waste */}
        <line x1="580" y1="135" x2="580" y2="220" stroke="#8B4513" strokeWidth="1.5" strokeDasharray="4 2" />
        <line x1="580" y1="220" x2="480" y2="220" stroke="#8B4513" strokeWidth="1.5" strokeDasharray="4 2" />
        <rect x="430" y="210" width="45" height="25" rx="3" fill="#0f172a" stroke="#8B4513" strokeWidth="1" />
        <text x="452" y="225" fill="#8B4513" fontSize="7" textAnchor="middle">WAS</text>

        {/* Legend */}
        <rect x="30" y="345" width="840" height="24" rx="4" fill="#0f172a" stroke="var(--rb-active)" strokeWidth="1" />
        <text x="45" y="359" fill="#f59e0b" fontSize="9">— Influent</text>
        <text x="110" y="359" fill="#06b6d4" fontSize="9">— Aeration / Air</text>
        <text x="210" y="359" fill="#8b5cf6" fontSize="9">— Clarifier</text>
        <text x="290" y="359" fill="#10b981" fontSize="9">— Effluent</text>
        <text x="360" y="359" fill="#f59e0b" fontSize="9">— RAS</text>
        <text x="410" y="359" fill="#8B4513" fontSize="9">— Waste Sludge</text>
        <text x="510" y="359" fill="#94a3b8" fontSize="9">F/M={fmt(safe.fm, 2)} | SRT={fmt(safe.srt, 1)}d | DO={fmt(safe.do, 1)} | OUR={fmt(safe.our, 1)}</text>
      </svg>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ */
export default function AerationSpecPage() {
  const [params, setParams] = useState<AerationParams>(PROFILES[0].defaults);
  const [saved, setSaved] = useState(false);
  const navigate = useNavigate();
  const [showFlow, setShowFlow] = useState(true);
  const [activeTab, setActiveTab] = useState<'influent' | 'tank' | 'kinetics' | 'aeration'>('influent');
  const [profileIndex, setProfileIndex] = useState(0);

  const size = useMemo(() => calcAeration(params), [params]);
  const sampleLoader = useSampleLoader<AerationParams>(setParams);

  const handleReset = () => { setParams(PROFILES[0].defaults); setProfileIndex(0); sampleLoader.clearSample(); toast.info('Parameters reset to defaults'); };
  const handleSave = () => { localStorage.setItem('aeration-params-v2', JSON.stringify(params)); setSaved(true); setTimeout(() => setSaved(false), 2000); toast.success('Configuration saved'); };

  useEffect(() => {
    const savedParams = localStorage.getItem('aeration-params-v2');
    if (savedParams) { try { setParams(JSON.parse(savedParams)); } catch {} }
  }, []);

  // Science accordion sections
  const scienceSections = useMemo(() => {
    const Q = params.flowRate;
    const q = Q / 24;
    const tankArea = params.tankLength * params.tankWidth;
    const workingVolume = tankArea * params.tankDepth;
    const detention = workingVolume / q;
    const T = params.temperature;
    const tempK = T + 273.15;
    const muWater = 0.001 * Math.exp(-1.5 * (T - 20) / tempK);
    const rhoWater = 998 - 0.2 * (T - 20);
    const S0 = params.influentBOD;
    const X = params.mlssTarget;
    const mu = params.maxGrowthRate * S0 / (params.halfSaturation + S0);
    const muCorrected = mu * Math.pow(1.07, T - 20);
    const U = (muCorrected / params.yieldCoeff) + params.decayRate;
    const fmRatio = (S0 * Q / 1000) / (X * workingVolume / 1000);
    const srt = 1 / Math.max(0.001, params.yieldCoeff * U - params.decayRate);
    const S_eff = params.halfSaturation * params.decayRate / (params.yieldCoeff * muCorrected - params.decayRate);
    const bodRemoval = Math.min(0.98, Math.max(0.1, 1 - S_eff / S0));
    const effluentBOD = S0 * (1 - bodRemoval);
    const codRemoval = bodRemoval * 0.92;
    const effluentCOD = params.influentCOD * (1 - codRemoval);
    const tssRemoval = Math.min(0.95, bodRemoval * 0.95);
    const effluentTSS = params.influentTSS * (1 - tssRemoval);
    const mu_nit = params.nitrificationRate * Math.pow(1.07, T - 20) * params.influentNH3 / (1 + params.influentNH3);
    const nit_removal = Math.min(0.99, 1 - 1 / (mu_nit * srt));
    const nitrificationEfficiency = nit_removal;
    const effluentNH3 = params.influentNH3 * (1 - nit_removal);
    const biomassInventory = X * workingVolume / 1000;
    const biomassProduction = params.yieldCoeff * (S0 - effluentBOD) * Q / 1000;
    const a_prime = 0.5;
    const b_prime = 0.1;
    const our_mg_L_h = a_prime * (S0 - effluentBOD) / 24 + b_prime * X / 24;
    const sor = our_mg_L_h * workingVolume / 1000 * 24;
    const sote = 0.25 * params.alphaFactor * Math.pow(1.02, 20 - T) * (params.tankDepth / 4);
    const sotr = sor / Math.max(sote, 0.05);
    const airFlowRate = (sotr / 24 / 60) / (1.2 * 0.232);
    const diffuserCount = Math.ceil((airFlowRate * 60) / 2);
    const blowerPower = (airFlowRate / 60 * 50000) / (0.75 * 1000);
    const mixerPower = 0.02 * workingVolume;
    const totalPower = blowerPower + mixerPower;
    return [
      {
        title: 'Tank Physics',
        icon: Wind,
        equations: [
          { label: 'Tank area', formula: 'A = L × W', substitution: `A = ${params.tankLength} × ${params.tankWidth} = ${sharedFmt(tankArea, 0)} m²`, result: sharedFmt(tankArea, 0), status: 'pass', unit: 'm²' },
          { label: 'Working volume', formula: 'V = A × depth', substitution: `V = ${sharedFmt(tankArea, 0)} × ${params.tankDepth} = ${sharedFmt(workingVolume, 0)} m³`, result: sharedFmt(workingVolume, 0), status: 'pass', unit: 'm³' },
          { label: 'Detention time', formula: 'τ = V / (Q/24)', substitution: `τ = ${sharedFmt(workingVolume, 0)} / ${sharedFmt(q, 1)} = ${sharedFmt(detention, 1)} h`, result: sharedFmt(detention, 1), status: detention >= 4 ? 'pass' : 'warn', unit: 'h' },
        ],
      },
      {
        title: 'Monod Kinetics & Biomass',
        icon: FlaskConical,
        equations: [
          { label: 'Growth rate μ', formula: 'μ = μmax × S / (Ks + S)', substitution: `μ = ${params.maxGrowthRate} × ${S0} / (${params.halfSaturation} + ${S0}) = ${sharedFmt(mu, 2)} d⁻¹`, result: sharedFmt(mu, 2), status: 'pass', unit: 'd⁻¹' },
          { label: 'Temp-corrected μ', formula: 'μ_T = μ × 1.07^(T−20)', substitution: `μ_T = ${sharedFmt(mu, 2)} × 1.07^${T - 20} = ${sharedFmt(muCorrected, 2)} d⁻¹`, result: sharedFmt(muCorrected, 2), status: 'pass', unit: 'd⁻¹' },
          { label: 'F/M ratio', formula: 'F/M = (S0×Q/1000) / (X×V/1000)', substitution: `F/M = (${S0}×${Q}/1000) / (${X}×${sharedFmt(workingVolume, 0)}/1000) = ${sharedFmt(fmRatio, 2)}`, result: sharedFmt(fmRatio, 2), status: fmRatio >= 0.05 && fmRatio <= 0.3 ? 'pass' : fmRatio >= 0.02 && fmRatio <= 0.5 ? 'warn' : 'fail', unit: 'kg/kg·d' },
          { label: 'SRT', formula: 'θc = 1 / (Y×U − kd)', substitution: `θc = 1 / (${params.yieldCoeff}×${sharedFmt(U, 3)} − ${params.decayRate}) = ${sharedFmt(srt, 1)} d`, result: sharedFmt(srt, 1), status: srt >= 3 ? 'pass' : srt >= 2 ? 'warn' : 'fail', unit: 'days' },
          { label: 'BOD removal', formula: 'η = 1 − S_eff/S0', substitution: `η = 1 − ${sharedFmt(S_eff, 1)}/${S0} = ${sharedFmt(bodRemoval * 100, 0)}%`, result: sharedFmt(bodRemoval * 100, 0), status: bodRemoval >= 0.85 ? 'pass' : bodRemoval >= 0.70 ? 'warn' : 'fail', unit: '%' },
          { label: 'COD removal', formula: 'η_COD = η_BOD × 0.92', substitution: `η_COD = ${sharedFmt(bodRemoval * 100, 0)}% × 0.92 = ${sharedFmt(codRemoval * 100, 0)}%`, result: sharedFmt(codRemoval * 100, 0), status: 'pass', unit: '%' },
          { label: 'TSS removal', formula: 'η_TSS = η_BOD × 0.95', substitution: `η_TSS = ${sharedFmt(bodRemoval * 100, 0)}% × 0.95 = ${sharedFmt(tssRemoval * 100, 0)}%`, result: sharedFmt(tssRemoval * 100, 0), status: 'pass', unit: '%' },
          { label: 'Biomass inventory', formula: 'X×V/1000', substitution: `${X}×${sharedFmt(workingVolume, 0)}/1000 = ${sharedFmt(biomassInventory, 0)} kg`, result: sharedFmt(biomassInventory, 0), status: 'pass', unit: 'kg' },
          { label: 'Biomass production', formula: 'Y×(S0−S)×Q/1000', substitution: `${params.yieldCoeff}×(${S0}−${sharedFmt(effluentBOD, 1)})×${Q}/1000 = ${sharedFmt(biomassProduction, 1)} kg/d`, result: sharedFmt(biomassProduction, 1), status: 'pass', unit: 'kg/d' },
        ],
      },
      {
        title: 'Nitrification',
        icon: FlaskConical,
        equations: [
          { label: 'Nitrifier growth', formula: 'μ_nit = μ_nit,max × 1.07^(T−20) × NH3/(1+NH3)', substitution: `μ_nit = ${params.nitrificationRate} × 1.07^${T - 20} × ${params.influentNH3}/${1 + params.influentNH3} = ${sharedFmt(mu_nit, 3)} d⁻¹`, result: sharedFmt(mu_nit, 3), status: 'pass', unit: 'd⁻¹' },
          { label: 'Nitrification efficiency', formula: 'η = 1 − 1/(μ_nit × θc)', substitution: `η = 1 − 1/(${sharedFmt(mu_nit, 3)} × ${sharedFmt(srt, 1)}) = ${sharedFmt(nitrificationEfficiency * 100, 0)}%`, result: sharedFmt(nitrificationEfficiency * 100, 0), status: nitrificationEfficiency >= 0.70 ? 'pass' : 'warn', unit: '%' },
          { label: 'Effluent NH₃', formula: 'NH3_out = NH3_in × (1 − η)', substitution: `${params.influentNH3} × (1 − ${sharedFmt(nitrificationEfficiency, 2)}) = ${sharedFmt(effluentNH3, 1)} mg/L`, result: sharedFmt(effluentNH3, 1), status: effluentNH3 <= 5 ? 'pass' : 'warn', unit: 'mg/L' },
        ],
      },
      {
        title: 'Oxygen Transfer & Power',
        icon: Droplets,
        equations: [
          { label: 'OUR', formula: 'OUR = 0.5×(S0−S)/24 + 0.1×X/24', substitution: `OUR = 0.5×(${S0}−${sharedFmt(effluentBOD, 1)})/24 + 0.1×${X}/24 = ${sharedFmt(our_mg_L_h, 1)} mg/L·h`, result: sharedFmt(our_mg_L_h, 1), status: 'pass', unit: 'mg/L·h' },
          { label: 'SOR', formula: 'SOR = OUR × V/1000 × 24', substitution: `SOR = ${sharedFmt(our_mg_L_h, 1)} × ${sharedFmt(workingVolume, 0)}/1000 × 24 = ${sharedFmt(sor, 1)} kg O₂/day`, result: sharedFmt(sor, 1), status: 'pass', unit: 'kg/day' },
          { label: 'SOTE', formula: 'SOTE = 0.25×α×1.02^(20−T)×(depth/4)', substitution: `SOTE = 0.25×${params.alphaFactor}×1.02^${20 - T}×(${params.tankDepth}/4) = ${sharedFmt(sote, 3)}`, result: sharedFmt(sote, 3), status: 'pass', unit: '' },
          { label: 'Air flow rate', formula: 'Q_air = (SOTR/24/60) / (ρ_air × 0.232)', substitution: `Q_air = (${sharedFmt(sotr, 1)}/24/60) / (1.2 × 0.232) = ${sharedFmt(airFlowRate, 1)} m³/min`, result: sharedFmt(airFlowRate, 1), status: 'pass', unit: 'm³/min' },
          { label: 'Diffuser count', formula: 'N = ceil(Q_air×60 / 2)', substitution: `N = ceil(${sharedFmt(airFlowRate, 1)}×60 / 2) = ${diffuserCount}`, result: String(diffuserCount), status: 'pass', unit: '' },
          { label: 'Blower power', formula: 'P = (Q_air/60 × ΔP) / (η × 1000)', substitution: `P = (${sharedFmt(airFlowRate, 1)}/60 × 50000) / (0.75 × 1000) = ${sharedFmt(blowerPower, 2)} kW`, result: sharedFmt(blowerPower, 2), status: 'pass', unit: 'kW' },
          { label: 'Mixer power', formula: 'P = 0.02 × V', substitution: `P = 0.02 × ${sharedFmt(workingVolume, 0)} = ${sharedFmt(mixerPower, 2)} kW`, result: sharedFmt(mixerPower, 2), status: 'pass', unit: 'kW' },
          { label: 'Total power', formula: 'P_total = P_blower + P_mixer', substitution: `P_total = ${sharedFmt(blowerPower, 2)} + ${sharedFmt(mixerPower, 2)} = ${sharedFmt(totalPower, 2)} kW`, result: sharedFmt(totalPower, 2), status: 'pass', unit: 'kW' },
        ],
      },
    ];
  }, [params, size]);

  const statusColors: any = {
    optimal: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle2 },
    acceptable: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', icon: Info },
    undersized: { text: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20', icon: AlertCircle },
    overloaded: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: ShieldAlert },
    oversized: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Info },
  };
  const sc = statusColors[size.status];
  const StatusIcon = sc.icon;

  return (
    <div className="max-w-[1440px] mx-auto space-y-5">
      <PageHeader
        icon={Wind}
        iconColor="text-emerald-400"
        title="Aeration Specification"
        subtitle="Activated sludge with Monod kinetics, OUR, oxygen transfer, and nitrification physics"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={handleReset} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-slate-800/80 border border-slate-800/60 text-slate-300 text-xs font-bold hover:bg-slate-700 hover:border-slate-600 transition-all"><RotateCcw className="w-3.5 h-3.5" />Reset</button>
            <button onClick={handleSave} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20 hover:bg-emerald-500/20 transition-all"><Save className="w-3.5 h-3.5" />{saved ? '✓ Saved' : 'Save'}</button>
            <button onClick={() => navigate('/equipment-validation?tab=aeration')} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-sky-500/10 text-sky-400 text-xs font-bold border border-sky-500/20 hover:bg-sky-500/20 transition-all"><Microscope className="w-3.5 h-3.5" />Validation</button>
          </div>
        }
      />

      {/* Status */}
      <div className={`rounded-xl border p-4 ${sc.bg} ${sc.border}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sc.bg}`}><StatusIcon className={`w-5 h-5 ${sc.text}`} /></div>
          <div><p className={`text-sm font-bold ${sc.text}`}>{size.status.toUpperCase()}</p><p className={`text-xs ${sc.text} opacity-80`}>{size.statusDetail}</p></div>
        </div>
      </div>

      {/* Profiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {PROFILES.map((profile, i) => (
          <PC key={i} profile={profile} isActive={profileIndex === i} onClick={() => { setParams(profile.defaults); setProfileIndex(i); toast.info(`Loaded ${profile.name} profile`); }} />
        ))}
      </div>

      {/* Sample Selector */}
      <SampleSelector loader={sampleLoader} />

      {/* Loaded sample readout */}
      {sampleLoader.baseSampleParams && (
        <div className="bg-rb-surface rounded-xl border border-sky-500/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Database className="w-4 h-4 text-sky-400" />Loaded Sample: Base → Current</h3>
            <span className="text-xs text-slate-500">Dilution: {sampleLoader.dilutionFactor.toFixed(1)}×</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-2 text-xs">
            {[
              { label: 'Flow', value: `${sharedFmt(params.flowRate, 0)} m³/d`, base: `${sharedFmt(sampleLoader.baseSampleParams.flowRate || 0, 0)}` },
              { label: 'pH', value: `${sharedFmt(params.ph, 1)}`, base: `${sharedFmt(sampleLoader.baseSampleParams.ph || 0, 1)}` },
              { label: 'Alk', value: `${sharedFmt(params.alkalinity, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.alkalinity || 0, 0)}` },
              { label: 'TSS', value: `${sharedFmt(params.influentTSS, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.tss || 0, 0)}` },
              { label: 'COD', value: `${sharedFmt(params.influentCOD, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.cod || 0, 0)}` },
              { label: 'O&G', value: `${sharedFmt(params.og, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.og || 0, 0)}` },
              { label: 'Temp', value: `${sharedFmt(params.temperature, 0)}°C`, base: `${sharedFmt(sampleLoader.baseSampleParams.temp || 0, 0)}°C` },
              { label: 'Turb', value: `${sharedFmt(params.turbidity, 0)} NTU`, base: `${sharedFmt(sampleLoader.baseSampleParams.turbidity || 0, 0)}` },
              { label: 'Cond', value: `${sharedFmt(params.conductivity, 0)} μS/cm`, base: `${sharedFmt(sampleLoader.baseSampleParams.conductivity || 0, 0)}` },
              { label: 'Hard', value: `${sharedFmt(params.hardness, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.hardness || 0, 0)}` },
              { label: 'NH₃-N', value: `${sharedFmt(params.nh3n, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.nh3n || 0, 0)}` },
              { label: 'TP', value: `${sharedFmt(params.tp, 1)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.tp || 0, 1)}` },
            ].map((item) => (
              <div key={item.label} className="p-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase">{item.label}</p>
                <p className="text-sm font-bold text-white">{item.value}</p>
                <p className="text-[10px] text-slate-600">Base: {item.base}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        {/* Flow Diagram */}
        <div className="xl:col-span-7 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Microscope className="w-4 h-4 text-emerald-400" />Process Flow</h3>
            <button onClick={() => setShowFlow(!showFlow)} className="text-[10px] text-slate-500 hover:text-slate-300 flex items-center gap-1"><Settings className="w-3 h-3" />{showFlow ? 'Collapse' : 'Expand'}</button>
          </div>
          <AnimatePresence>{showFlow && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
              <FlowDiagram params={params} size={size} />
            </motion.div>
          )}</AnimatePresence>
        </div>

        {/* Controls & Results */}
        <div className="xl:col-span-5 space-y-4">
          {/* Gauges */}
          <div className="p-4 rb-card">
            <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Gauge className="w-4 h-4 text-emerald-400" />Performance Gauges</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <G value={size.bodRemoval * 100} max={100} label="BOD Removal" unit="%" color="#10b981" />
              <G value={size.codRemoval * 100} max={100} label="COD Removal" unit="%" color="#06b6d4" />
              <G value={size.nitrificationEfficiency * 100} max={100} label="Nitrification" unit="%" color="#8b5cf6" />
              <G value={size.our} max={20} label="OUR" unit="mg/L·h" color="#f59e0b" />
              <G value={size.fmRatio} max={1} label="F/M Ratio" unit="kg/kg·d" color="#ef4444" />
              <G value={size.srt} max={30} label="SRT" unit="days" color="#a855f7" />
              <G value={size.totalPower} max={50} label="Total Power" unit="kW" color="#f97316" />
              <G value={size.effluentBOD} max={100} label="Effluent BOD" unit="mg/L" color="#22c55e" />
            </div>
          </div>

          {/* Tabs */}
          <div className="overflow-hidden rb-card">
            <div className="flex border-b border-slate-800">
              {(['influent', 'tank', 'kinetics', 'aeration'] as const).map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-2.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${activeTab === tab ? 'text-emerald-400 border-b-2 border-emerald-400 bg-emerald-500/5' : 'text-slate-500 hover:text-slate-300'}`}>
                  {tab === 'influent' ? 'Influent' : tab === 'tank' ? 'Tank Design' : tab === 'kinetics' ? 'Kinetics' : 'Aeration'}
                </button>
              ))}
            </div>
            <div className="p-4 space-y-4">
              {activeTab === 'influent' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <S label="Flow Rate" value={params.flowRate} min={24} max={2400} step={12} unit="m³/day" icon={Waves} color="#06b6d4" onChange={(v: number) => setParams({ ...params, flowRate: v })} />
                  <S label="Influent BOD" value={params.influentBOD} min={100} max={5000} step={50} unit="mg/L" icon={FlaskConical} color="#f59e0b" onChange={(v: number) => setParams({ ...params, influentBOD: v })} />
                  <S label="Influent COD" value={params.influentCOD} min={200} max={8000} step={100} unit="mg/L" icon={Beaker} color="#f59e0b" onChange={(v: number) => setParams({ ...params, influentCOD: v })} />
                  <S label="Influent TSS" value={params.influentTSS} min={50} max={2000} step={50} unit="mg/L" icon={Filter} color="#f59e0b" onChange={(v: number) => setParams({ ...params, influentTSS: v })} />
                  <S label="Influent TKN" value={params.influentTKN} min={10} max={300} step={5} unit="mg/L" icon={FlaskConical} color="#a855f7" onChange={(v: number) => setParams({ ...params, influentTKN: v })} />
                  <S label="Influent NH₃" value={params.influentNH3} min={5} max={150} step={5} unit="mg/L" icon={Droplets} color="#a855f7" onChange={(v: number) => setParams({ ...params, influentNH3: v })} />
                  <S label="Influent pH" value={params.influentpH} min={5} max={9} step={0.1} unit="" icon={Beaker} color="#a855f7" onChange={(v: number) => setParams({ ...params, influentpH: v })} />
                  <S label="Temperature" value={params.temperature} min={5} max={35} step={1} unit="°C" icon={Thermometer} color="#ef4444" onChange={(v: number) => setParams({ ...params, temperature: v })} />
                  <S label="O&G" value={params.og} min={20} max={1000} step={10} unit="mg/L" icon={Droplets} color="#f59e0b" onChange={(v: number) => setParams({ ...params, og: v })} />
                  <S label="pH (sample)" value={params.ph} min={4} max={9.5} step={0.1} unit="" icon={Beaker} color="#a855f7" onChange={(v: number) => setParams({ ...params, ph: v })} />
                  <S label="Alkalinity" value={params.alkalinity} min={10} max={500} step={5} unit="mg/L CaCO₃" icon={FlaskConical} color="#10b981" onChange={(v: number) => setParams({ ...params, alkalinity: v })} />
                  <S label="Turbidity" value={params.turbidity} min={10} max={2000} step={10} unit="NTU" icon={Filter} color="#f59e0b" onChange={(v: number) => setParams({ ...params, turbidity: v })} />
                  <S label="Conductivity" value={params.conductivity} min={100} max={10000} step={50} unit="μS/cm" icon={Zap} color="#fbbf24" onChange={(v: number) => setParams({ ...params, conductivity: v })} />
                  <S label="Hardness" value={params.hardness} min={10} max={1000} step={10} unit="mg/L CaCO₃" icon={Droplets} color="#3b82f6" onChange={(v: number) => setParams({ ...params, hardness: v })} />
                  <S label="NH₃-N" value={params.nh3n} min={1} max={200} step={1} unit="mg/L" icon={Beaker} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, nh3n: v })} />
                  <S label="Total Phosphorus" value={params.tp} min={0.5} max={50} step={0.5} unit="mg/L" icon={FlaskConical} color="#ec4899" onChange={(v: number) => setParams({ ...params, tp: v })} />
                </div>
              )}
              {activeTab === 'tank' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <S label="Tank Length" value={params.tankLength} min={5} max={30} step={0.5} unit="m" icon={ArrowRight} color="#06b6d4" onChange={(v: number) => setParams({ ...params, tankLength: v })} />
                  <S label="Tank Width" value={params.tankWidth} min={3} max={15} step={0.5} unit="m" icon={ArrowRight} color="#06b6d4" onChange={(v: number) => setParams({ ...params, tankWidth: v })} />
                  <S label="Tank Depth" value={params.tankDepth} min={2} max={8} step={0.1} unit="m" icon={ArrowDown} color="#06b6d4" onChange={(v: number) => setParams({ ...params, tankDepth: v })} />
                  <S label="Freeboard" value={params.freeboard} min={0.3} max={1.5} step={0.1} unit="m" icon={ArrowDown} color="#06b6d4" onChange={(v: number) => setParams({ ...params, freeboard: v })} />
                  <S label="MLSS Target" value={params.mlssTarget} min={1000} max={5000} step={100} unit="mg/L" icon={CircleDot} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, mlssTarget: v })} />
                </div>
              )}
              {activeTab === 'kinetics' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <S label="Yield Coefficient" value={params.yieldCoeff} min={0.3} max={0.8} step={0.05} unit="kg MLSS/kg BOD" icon={TrendingUp} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, yieldCoeff: v })} />
                  <S label="Decay Rate" value={params.decayRate} min={0.02} max={0.15} step={0.01} unit="d⁻¹" icon={Timer} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, decayRate: v })} />
                  <S label="Half-Saturation Ks" value={params.halfSaturation} min={10} max={150} step={5} unit="mg/L" icon={Beaker} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, halfSaturation: v })} />
                  <S label="Max Growth Rate μmax" value={params.maxGrowthRate} min={1} max={8} step={0.5} unit="d⁻¹" icon={TrendingUp} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, maxGrowthRate: v })} />
                  <S label="Nitrification Rate" value={params.nitrificationRate} min={0.1} max={0.8} step={0.05} unit="d⁻¹" icon={FlaskConical} color="#a855f7" onChange={(v: number) => setParams({ ...params, nitrificationRate: v })} />
                  <div className="flex items-center gap-2 p-2 bg-rb-elevated rounded-lg">
                    <input type="checkbox" checked={params.nitrificationEnabled} onChange={(e) => setParams({ ...params, nitrificationEnabled: e.target.checked })} className="accent-emerald-500" />
                    <span className="text-xs text-slate-300">Enable Nitrification</span>
                  </div>
                </div>
              )}
              {activeTab === 'aeration' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <S label="DO Setpoint" value={params.doTarget} min={0.5} max={4} step={0.1} unit="mg/L" icon={Droplets} color="#06b6d4" onChange={(v: number) => setParams({ ...params, doTarget: v })} />
                  <S label="Alpha Factor" value={params.alphaFactor} min={0.3} max={1.0} step={0.05} unit="" icon={Gauge} color="#06b6d4" onChange={(v: number) => setParams({ ...params, alphaFactor: v })} />
                </div>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="p-4 space-y-4 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-400" />Results & Performance</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 space-y-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Effluent Quality</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">BOD</span><span className="text-white font-bold">{fmt(params.influentBOD, 0)} → {fmt(size.effluentBOD, 0)} mg/L</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">COD</span><span className="text-white font-bold">{fmt(params.influentCOD, 0)} → {fmt(size.effluentCOD, 0)} mg/L</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">TSS</span><span className="text-white font-bold">{fmt(params.influentTSS, 0)} → {fmt(size.effluentTSS, 0)} mg/L</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">NH₃</span><span className="text-white font-bold">{fmt(params.influentNH3, 0)} → {fmt(size.effluentNH3, 0)} mg/L</span></div>
                </div>
              </div>
              <div className="p-3 space-y-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Biomass & Process</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">Inventory</span><span className="text-amber-400 font-bold">{fmt(size.biomassInventory, 0)} kg MLSS</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Production</span><span className="text-amber-400 font-bold">{fmt(size.biomassProduction, 1)} kg/d</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">F/M Ratio</span><span className="text-emerald-400 font-bold">{fmt(size.fmRatio, 2)} kg/kg·d</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">SRT</span><span className="text-emerald-400 font-bold">{fmt(size.srt, 1)} days</span></div>
                </div>
              </div>
              <div className="p-3 space-y-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Power & Oxygen</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">Blower</span><span className="text-sky-400 font-bold">{fmt(size.blowerPower, 2)} kW</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Mixer</span><span className="text-sky-400 font-bold">{fmt(size.mixerPower, 2)} kW</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Total</span><span className="text-emerald-400 font-bold">{fmt(size.totalPower, 2)} kW</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Air Flow</span><span className="text-sky-400 font-bold">{fmt(size.airFlowRate, 1)} m³/min</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Design Notes */}
          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-400" />Design Notes & Warnings</h3>
            <div className="space-y-2">
              {size.designNotes.map((note, i) => (
                <div key={i} className={`text-xs p-2 rounded-lg ${note.startsWith('⚠️') ? 'bg-rose-500/5 border border-rose-500/20 text-rose-400' : note.startsWith('ℹ️') ? 'bg-sky-500/5 border border-sky-500/20 text-sky-400' : 'bg-emerald-500/5 border border-emerald-500/20 text-emerald-400'}`}>{note}</div>
              ))}
            </div>
          </div>

          {/* Science Validation Accordion */}
          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Microscope className="w-4 h-4 text-sky-400" />Science Validation</h3>
            <ScienceAccordion sections={scienceSections} />
          </div>
        </div>
      </div>
    </div>
  );
}
